export type Waveform = 'sine' | 'square' | 'triangle' | 'sawtooth';

export const DEFAULT_SAMPLE_RATE = 44100;

export interface ToneOptions {
  frequency: number;
  durationSeconds: number;
  sampleRate?: number;
  amplitude?: number;
  waveform?: Waveform;
  attack?: number;
  release?: number;
}

function oscillator(waveform: Waveform, phase: number): number {
  switch (waveform) {
    case 'sine':
      return Math.sin(phase);
    case 'square':
      return Math.sin(phase) >= 0 ? 1 : -1;
    case 'triangle':
      return (2 / Math.PI) * Math.asin(Math.sin(phase));
    case 'sawtooth':
      return 2 * (phase / (2 * Math.PI) - Math.floor(0.5 + phase / (2 * Math.PI)));
  }
}

function envelopeAt(index: number, total: number, attackSamples: number, releaseSamples: number) {
  const rising = index < attackSamples ? index / attackSamples : 1;
  const remaining = total - index - 1;
  const falling = remaining < releaseSamples ? remaining / releaseSamples : 1;
  return Math.min(rising, falling);
}

export function synthTone({
  frequency,
  durationSeconds,
  sampleRate = DEFAULT_SAMPLE_RATE,
  amplitude = 0.6,
  waveform = 'sine',
  attack = 0.005,
  release = 0.04,
}: ToneOptions): Float32Array {
  const total = Math.max(1, Math.round(durationSeconds * sampleRate));
  const attackSamples = Math.max(1, Math.round(attack * sampleRate));
  const releaseSamples = Math.max(1, Math.round(release * sampleRate));
  const samples = new Float32Array(total);

  for (let i = 0; i < total; i++) {
    const phase = (2 * Math.PI * frequency * i) / sampleRate;
    samples[i] =
      oscillator(waveform, phase) * amplitude * envelopeAt(i, total, attackSamples, releaseSamples);
  }

  return samples;
}

export function mix(tracks: readonly Float32Array[]): Float32Array {
  const length = tracks.reduce((longest, track) => Math.max(longest, track.length), 0);
  const mixed = new Float32Array(length);

  for (let i = 0; i < length; i++) {
    let sum = 0;
    for (const track of tracks) sum += track[i] ?? 0;
    mixed[i] = sum;
  }

  return clampToUnit(mixed);
}

export function sequence(parts: readonly Float32Array[]): Float32Array {
  const length = parts.reduce((sum, part) => sum + part.length, 0);
  const joined = new Float32Array(length);

  let offset = 0;
  for (const part of parts) {
    joined.set(part, offset);
    offset += part.length;
  }

  return joined;
}

function clampToUnit(samples: Float32Array): Float32Array {
  let peak = 0;
  for (const sample of samples) peak = Math.max(peak, Math.abs(sample));
  if (peak <= 1) return samples;

  const scale = 1 / peak;
  for (let i = 0; i < samples.length; i++) samples[i]! *= scale;
  return samples;
}
